// Rail — view-navigation tabs (US-874 · R-4, corrected 2026-06-25).
//
// 72px vertical rail mirroring the mockup's `.rail`. Each item is a tab that
// switches the left panel's active view (Analysis / Map Config / Definitions /
// About) via UIContext. The panel renders the matching view; this component only
// owns the active-tab affordance, not the view content.
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import useUI from "../../hooks/useUI";

// Rail tabs, in mockup order. `id` matches UIContext RAIL_VIEWS.
const TABS = [
  { id: "analysis", label: "Analysis", Icon: BarChartRoundedIcon },
  { id: "map-config", label: "Map Config", Icon: LayersRoundedIcon },
  { id: "definitions", label: "Definitions", Icon: InfoOutlinedIcon },
  { id: "about", label: "About", Icon: HelpOutlineRoundedIcon },
];

const Rail = () => {
  const { tokens } = useTheme();
  const { layout, spacing } = tokens;
  const { activeView, setActiveView } = useUI();

  return (
    <Box
      component="nav"
      aria-label="View navigation"
      data-region="rail"
      sx={{
        gridArea: "rail",
        width: `${layout.railW}px`,
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        pt: spacing.sm,
        zIndex: 20,
      }}
    >
      {TABS.map(({ id, label, Icon }) => {
        const active = activeView === id;
        return (
          <ButtonBase
            key={id}
            aria-label={label}
            aria-current={active ? "page" : undefined}
            onClick={() => setActiveView(id)}
            sx={{
              position: "relative",
              width: "100%",
              py: "12px",
              px: "4px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              color: active ? "primary.main" : "text.secondary",
              transition: "color 0.15s ease",
              "&:hover": { color: "primary.main", bgcolor: "primary.light" },
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "-2px",
              },
              ...(active && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "8px",
                  bottom: "8px",
                  width: "3px",
                  bgcolor: "primary.main",
                },
              }),
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{ fontSize: 9, lineHeight: 1, fontWeight: active ? 600 : 500 }}
            >
              {label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default Rail;
